import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Boton from '../../components/Boton'
import BuscadorPedido from '../../components/BuscadorPedido'
import { npApi } from '../../api/compras'
import { fmtFecha } from '../../utils/fecha'
import { colors, text, space, font } from '../../design/tokens'
import { nuevoUuid, mensajeError, st } from '../emp900/_helpers'
import { Badge, CajaError, CajaOk, Campo } from '../emp900/_comun'
import { NP_ESTADO_LABEL, NP_ESTADO_TONO, MOTIVO_CIERRE, fmtMoneda, fmtCant, fmtPct } from './_np'

/**
 * COMPRAS → Pedidos → Modificación / Anulación / Cancelación. Camino de
 * Martin (2 sep 2026), como en Presea: se tipea el Nº de pedido (o lupa), el
 * pedido aparece en pantalla y se opera ahí mismo.
 *   Modificación: solo borradores → abre el formulario de ese pedido.
 *   Anulación:    el pedido ENTERO (borrador, o emitido sin recepciones), con motivo.
 *   Cancelación:  el SALDO pendiente de los renglones marcados de un pedido emitido, con motivo.
 */
const OPS = {
  modificacion: {
    titulo: 'Modificación de pedidos', sub: 'Solo se modifica un pedido en borrador. Uno ya emitido no se reescribe: se le hace una Nueva versión desde Consultas.',
    filtros: { estado: 'borrador' }, elegible: p => p.estado === 'borrador', ayuda: 'Pedidos en borrador',
  },
  anulacion: {
    titulo: 'Anulación de pedidos', sub: 'Se anula el pedido completo. Vale para borradores y para emitidos que todavía no tuvieron recepciones. Nada se borra: queda anulado con su motivo.',
    filtros: {}, elegible: p => ['borrador', 'emitido'].includes(p.estado), ayuda: 'Borradores y emitidos sin recepciones',
  },
  cancelacion: {
    titulo: 'Cancelación de pedidos', sub: 'Se cancela el saldo pendiente de uno o más renglones de un pedido emitido (lo que el proveedor no va a entregar). Lo ya recibido no se toca.',
    filtros: { abiertos: 1 }, elegible: p => p.estado === 'emitido' && (p.saldo_unidades == null || Number(p.saldo_unidades) > 0), ayuda: 'Emitidos con saldo pendiente',
  },
}

export default function PedidoOperacionPage({ op }) {
  const navigate = useNavigate()
  const cfg = OPS[op]
  const [p, setP] = useState(null)
  const [ok, setOk] = useState('')
  const [error, setError] = useState('')
  const [trabajando, setTrabajando] = useState(false)
  const elegible = useMemo(() => cfg.elegible, [cfg])

  const cargar = (pedido) => {
    setOk(''); setError('')
    if (op === 'modificacion') {
      if (pedido.estado !== 'borrador') { setError(`El pedido ${pedido.numero} está ${NP_ESTADO_LABEL[pedido.estado]}: solo se modifican borradores.`); return }
      navigate(`/compras/pedidos/${pedido.id}/editar`)
      return
    }
    if (op === 'anulacion' && (pedido.recepciones || []).length > 0) { setError(`El pedido ${pedido.numero} ya tiene recepciones: no se anula. Cancelá el saldo que falte desde Cancelación.`); return }
    setP(pedido)
  }

  const ejecutar = async (fn, mensaje) => {
    setTrabajando(true); setError('')
    try { await fn(); setOk(mensaje); setP(null) } catch (e) { setError(mensajeError(e)) } finally { setTrabajando(false) }
  }

  return (
    <div>
      <div style={st.header}><h2 style={st.title}>{cfg.titulo}</h2></div>
      <p style={st.sub}>{cfg.sub}</p>

      <div style={st.card}>
        <BuscadorPedido key={op} onCargar={cargar} elegible={elegible} filtros={cfg.filtros} ayuda={cfg.ayuda} />
      </div>
      {ok && <div style={{ marginTop: space.lg }}><CajaOk titulo="Listo">{ok}</CajaOk></div>}
      {error && <div style={{ marginTop: space.lg }}><CajaError>{error}</CajaError></div>}

      {p && <ResumenPedido p={p} />}
      {p && op === 'anulacion' && <Anulacion p={p} trabajando={trabajando} onAnular={(motivo) => ejecutar(() => npApi.anular(p.id, { op_uuid: nuevoUuid(), motivo }), `Pedido ${p.numero} anulado.`)} />}
      {p && op === 'cancelacion' && <Cancelacion p={p} trabajando={trabajando} onCancelar={(lineas) => ejecutar(() => npApi.cancelarSaldo(p.id, { op_uuid: nuevoUuid(), lineas }), `Saldo cancelado en ${lineas.length} renglón(es) del pedido ${p.numero}.`)} />}
    </div>
  )
}

function ResumenPedido({ p }) {
  const mon = p.moneda || 'ARS'
  const dato = (label, valor) => <div><div style={{ fontSize: text.meta, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div><div style={{ fontSize: text.body }}>{valor || '—'}</div></div>
  return (
    <div style={{ ...st.card, marginTop: space.lg }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: space.md, marginBottom: space.md }}>
        <span style={{ fontFamily: font.mono, fontSize: text.title, fontWeight: 600 }}>{p.numero}</span>
        <Badge tono={NP_ESTADO_TONO[p.estado] || 'neutro'}>{NP_ESTADO_LABEL[p.estado] || p.estado}</Badge>
        <span style={{ color: colors.textSec, fontSize: text.sec }}>{p.proveedor_nombre} · {fmtFecha(p.fecha)}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: space.lg }}>
        {dato('Condición', p.condicion_pago)}
        {dato('Moneda', mon)}
        {dato('Nº pedido del proveedor', p.numero_pedido_proveedor)}
        {dato('Total', fmtMoneda(p.total, mon))}
      </div>
    </div>
  )
}

function Anulacion({ p, trabajando, onAnular }) {
  const [motivo, setMotivo] = useState('')
  const mon = p.moneda || 'ARS'
  return (
    <div style={{ ...st.card, marginTop: space.lg }}>
      <div style={st.cardTitulo}>Renglones del pedido</div>
      <table style={st.table}>
        <thead><tr>{['Código', 'Detalle', 'Cantidad', 'Precio', 'Total'].map(h => <th key={h} style={{ ...st.th, ...(['Cantidad', 'Precio', 'Total'].includes(h) ? { textAlign: 'right' } : {}) }}>{h}</th>)}</tr></thead>
        <tbody>{(p.items || []).map(it => (
          <tr key={it.id} style={st.tr}><td style={st.tdMono}>{it.sku}</td><td style={st.td}>{it.descripcion}</td><td style={st.tdImporte}>{fmtCant(it.cantidad)}</td><td style={st.tdImporte}>{fmtMoneda(it.precio ?? it.costo_unitario_neto, mon, 4)}</td><td style={st.tdImporte}>{fmtMoneda(it.costo_final, mon)}</td></tr>
        ))}</tbody>
      </table>
      <div style={{ ...st.grid2, marginTop: space.lg }}>
        <Campo label="Motivo de la anulación"><input style={st.input} value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus /></Campo>
      </div>
      <div style={st.pie}>
        <Boton variante="destructive" disabled={motivo.trim().length < 3 || trabajando} onClick={() => onAnular(motivo)}>{trabajando ? 'Anulando…' : `Anular el pedido ${p.numero}`}</Boton>
      </div>
    </div>
  )
}

function Cancelacion({ p, trabajando, onCancelar }) {
  const conSaldo = (p.items || []).filter(it => Number(it.saldo) > 0)
  const [sel, setSel] = useState(() => new Set())
  const [motivo, setMotivo] = useState('proveedor_sin_stock')
  const [comentario, setComentario] = useState('')
  const toggle = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const listo = sel.size > 0 && (motivo !== 'otro' || comentario.trim())
  return (
    <div style={{ ...st.card, marginTop: space.lg }}>
      <div style={st.cardTitulo}>Renglones con saldo pendiente — marcá los que se cancelan</div>
      {conSaldo.length === 0 ? <p style={st.info}>Este pedido no tiene saldo pendiente.</p> : (
        <table style={st.table}>
          <thead><tr><th style={st.th}><input type="checkbox" checked={sel.size === conSaldo.length} onChange={e => setSel(e.target.checked ? new Set(conSaldo.map(i => i.id)) : new Set())} /></th>{['Código', 'Detalle', 'Pedido', 'Recibido', 'Saldo a cancelar', 'Dto1/2/3'].map(h => <th key={h} style={{ ...st.th, ...(['Pedido', 'Recibido', 'Saldo a cancelar'].includes(h) ? { textAlign: 'right' } : {}) }}>{h}</th>)}</tr></thead>
          <tbody>{conSaldo.map(it => (
            <tr key={it.id} style={st.trClick} onClick={() => toggle(it.id)}>
              <td style={st.td}><input type="checkbox" checked={sel.has(it.id)} onChange={() => toggle(it.id)} onClick={e => e.stopPropagation()} /></td>
              <td style={st.tdMono}>{it.sku}</td><td style={st.td}>{it.descripcion}</td>
              <td style={st.tdImporte}>{fmtCant(it.cantidad)}</td><td style={st.tdImporte}>{fmtCant(it.cantidad_recibida)}</td><td style={st.tdImporte}>{fmtCant(it.saldo)}</td>
              <td style={{ ...st.td, color: colors.textMeta, fontSize: text.sec }}>{[it.descuento1_pct ?? it.descuento_pct, it.descuento2_pct, it.descuento3_pct].map(fmtPct).join(' · ')}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      <div style={{ ...st.grid2, marginTop: space.lg }}>
        <Campo label="Motivo"><select style={st.input} value={motivo} onChange={e => setMotivo(e.target.value)}>{Object.entries(MOTIVO_CIERRE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Campo>
        <Campo label={motivo === 'otro' ? 'Comentario (obligatorio)' : 'Comentario'}><input style={st.input} value={comentario} onChange={e => setComentario(e.target.value)} /></Campo>
      </div>
      <div style={st.pie}>
        <Boton variante="primary" disabled={!listo || trabajando} onClick={() => onCancelar([...sel].map(item_id => ({ item_id, motivo, comentario: comentario || undefined })))}>{trabajando ? 'Cancelando…' : `Cancelar saldo de ${sel.size} renglón(es)`}</Boton>
      </div>
    </div>
  )
}
